import { QueryKeys } from "@/constants/Query";
import { useApi } from "@/hooks/useApi";
import axiosInstance from "@/lib/axios";

export enum AOEQuestionAnswerType {
  DATE = "date",
  TEXT = "text",
  CODING = "coding",
  TEXT_OR_CODING = "text_or_coding",
}

export enum AOEQuestionItemControl {
  DROPDOWN = "dropdown",
  EDITABLE_DROPDOWN = "editableDropdown",
  RADIO = "radioButtons",
  MULTISELECT = "multiselect",
  TEXT = "textBox",
  CHECKBOX = "checkBox",
  DATE = "localDate",
}

interface RetrieveAOEForOrderParams {
  patientId: string;
  requestGroupId: string;
}

export interface AOEAnswerOption {
  label: string;
  valueCoding?: { code: string; display: string };
  valueString?: string;
}

export interface AOEQuestion {
  question: string;
  answerType: AOEQuestionAnswerType;
  linkId: string;
  required: boolean;
  itemControl?: AOEQuestionItemControl;
  answerOptions?: AOEAnswerOption[];
}

export interface AOEAnswerCoding {
  code: string;
  display: string;
}

export interface AOEAnswerValue {
  answerType: AOEQuestionAnswerType;
  answerValue: string | AOEAnswerCoding;
}

export interface AOEAnswer {
  linkId: string;
  question: string;
  answerValues: AOEAnswerValue[];
}

export interface AOEQuestionnaire {
  questionnaireResponseId: string;
  serviceRequestId: string;
  testName: string;
  questions: AOEQuestion[];
  answers: AOEAnswer[];
  requiredQuestionsAnswered: boolean;
}

interface RetrieveAOEForOrderResponse {
  aoeQuestionnaires: AOEQuestionnaire[];
  requiredAoeQuestionsAnswered: boolean;
}

export function isAOEAnswerCoding(value: unknown): value is AOEAnswerCoding {
  if (typeof value !== "object" || value === null) {
    return false;
  }

  return "code" in value && "display" in value;
}

export const useRetrieveAOEForOrder = ({
  patientId,
  requestGroupId,
}: RetrieveAOEForOrderParams) => {
  return useApi<RetrieveAOEForOrderResponse>({
    queryOptions: {
      enabled: Boolean(requestGroupId),
      queryFn: async () => {
        const response = await axiosInstance.get<RetrieveAOEForOrderResponse>(
          `/labs/order/v2/${requestGroupId}/aoe`,
        );
        return response.data;
      },
      queryKey: QueryKeys.Patient.Chart.Labs.AOE_FOR_ORDER({
        patientId,
        requestGroupId,
      }),
    },
  });
};
